/**
 * Hierarchy types — the shared shape over every structural tree of real
 * entities (org units, product nodes, …), as distinct from item relationship
 * types (Cluster, Dependency, Contains, …) which are gate-bearing edges
 * BETWEEN ITEMS and live in canon 12/13.
 *
 * Spec: docs/9-exploration/HIERARCHY-TYPES-ARCHITECTURE-BRIEF.md §2, §5.
 *
 * Present kinds:
 *   org      — `org_units` (Present since the org chart shipped)
 *   product  — `product_nodes` (migration 719)
 *
 * Deliberately absent: `team`. Brief §5.3 leaves the team model (org subtree
 * vs separate tree vs groups) as an owner decision; until it is made,
 * participation references the ORG hierarchy, which is what the brief's
 * Phase 1 specifies.
 *
 * Rule for builders (brief §2.3): organizational and portfolio structure →
 * a hierarchy kind here. Work-to-work constraints → item relationship types.
 * Never model a product tree with item `Contains` edges.
 *
 * The tables stay parallel (brief §5.1, first-slice recommendation); this
 * module is the one interface both are read through, so tree utilities are
 * written once.
 */

import type { OrgUnit } from '../../types/prioritization/enterpriseIdentity';

// ── Kinds ─────────────────────────────────────────────────────────────────────

export type HierarchyKind = 'org' | 'product';

export interface HierarchyKindDefinition {
  key: HierarchyKind;
  /** Human label, singular ("Org unit"). */
  nodeLabel: string;
  /** Human label for the whole tree ("Org hierarchy"). */
  treeLabel: string;
  /** Backing table. Parallel tables, one interface (brief §5.1). */
  table: 'org_units' | 'product_nodes';
  /** The item column that references a node of this kind, if any. */
  itemReference: 'orgUnitId' | 'productNodeId' | null;
  /** Workspace module that gates the kind's admin surface. */
  moduleKey: 'org_hierarchy' | 'product_hierarchy';
  /** The question a node of this kind answers (brief §2.2). */
  answers: string;
}

export const HIERARCHY_KINDS: Readonly<Record<HierarchyKind, HierarchyKindDefinition>> = {
  org: {
    key: 'org',
    nodeLabel: 'Org unit',
    treeLabel: 'Org hierarchy',
    table: 'org_units',
    itemReference: 'orgUnitId',
    moduleKey: 'org_hierarchy',
    answers: 'Where does this entity sit for reporting, permission scope, Catchball tiers and intake queues?',
  },
  product: {
    key: 'product',
    nodeLabel: 'Product node',
    treeLabel: 'Product hierarchy',
    table: 'product_nodes',
    itemReference: 'productNodeId',
    moduleKey: 'product_hierarchy',
    answers: 'Where does this work live in the portfolio — which product line, product or component?',
  },
};

export const HIERARCHY_KIND_KEYS: readonly HierarchyKind[] = ['org', 'product'];

// ── Product node level labels ─────────────────────────────────────────────────

export type ProductNodeKind = 'portfolio' | 'product_line' | 'product' | 'component';

export const PRODUCT_NODE_KINDS: readonly ProductNodeKind[] = [
  'portfolio',
  'product_line',
  'product',
  'component',
];

export const PRODUCT_NODE_KIND_LABELS: Readonly<Record<ProductNodeKind, string>> = {
  portfolio: 'Portfolio',
  product_line: 'Product line',
  product: 'Product',
  component: 'Component',
};

/** The level a child conventionally takes under a given parent level. */
export function defaultChildProductKind(parentKind: ProductNodeKind | null): ProductNodeKind {
  switch (parentKind) {
    case null:
      return 'portfolio';
    case 'portfolio':
      return 'product_line';
    case 'product_line':
      return 'product';
    case 'product':
    case 'component':
      return 'component';
  }
}

// ── The shared node shape ─────────────────────────────────────────────────────

/**
 * One node of any hierarchy kind. Every field a tree utility needs, nothing
 * kind-specific: kind-specific data stays on the source row (`OrgUnit`,
 * `ProductNode`) and is reached through `source`.
 */
export interface HierarchyNode {
  id: string;
  kind: HierarchyKind;
  workspaceId: string | null;
  parentId: string | null;
  name: string;
  /** Level label as stored ("department", "product_line", …). */
  levelKey: string;
  /** 0 for a root. */
  depth: number;
  /** Ancestor ids root-first, including self. */
  path: string[];
  active: boolean;
  sortOrder: number;
}

export interface HierarchyTreeNode<N extends HierarchyNode = HierarchyNode> {
  node: N;
  children: HierarchyTreeNode<N>[];
}

// ── Adapters ──────────────────────────────────────────────────────────────────

export function orgUnitToHierarchyNode(unit: OrgUnit): HierarchyNode {
  return {
    id: unit.id,
    kind: 'org',
    workspaceId: unit.workspaceId ?? null,
    parentId: unit.parentId ?? null,
    name: unit.displayName || unit.name,
    levelKey: unit.type,
    depth: unit.depth ?? 0,
    path: unit.path ?? [unit.id],
    active: unit.active !== false,
    sortOrder: 0,
  };
}

/** Row shape of `product_nodes` after camelCasing (see productNodesService). */
export interface ProductNode {
  id: string;
  workspaceId: string;
  parentId: string | null;
  name: string;
  kind: ProductNodeKind;
  description: string | null;
  externalId: string | null;
  path: string[];
  depth: number;
  sortOrder: number;
  active: boolean;
  metadata: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export function productNodeToHierarchyNode(node: ProductNode): HierarchyNode {
  return {
    id: node.id,
    kind: 'product',
    workspaceId: node.workspaceId,
    parentId: node.parentId,
    name: node.name,
    levelKey: node.kind,
    depth: node.depth,
    path: node.path.length > 0 ? node.path : [node.id],
    active: node.active,
    sortOrder: node.sortOrder,
  };
}

// ── Tree utilities (kind-agnostic) ────────────────────────────────────────────

function compareSiblings(a: HierarchyNode, b: HierarchyNode): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return a.name.localeCompare(b.name);
}

/**
 * Build a forest from a flat node list. A node whose parent is not in the
 * list is treated as a root (the org-chart normalizer does the same), so a
 * scoped fetch never loses nodes.
 */
export function buildHierarchyTree<N extends HierarchyNode>(nodes: readonly N[]): HierarchyTreeNode<N>[] {
  const ids = new Set(nodes.map((n) => n.id));
  const byParent = new Map<string | null, N[]>();
  for (const n of nodes) {
    const key = n.parentId && ids.has(n.parentId) ? n.parentId : null;
    const list = byParent.get(key) ?? [];
    list.push(n);
    byParent.set(key, list);
  }
  const build = (parentId: string | null, seen: Set<string>): HierarchyTreeNode<N>[] =>
    (byParent.get(parentId) ?? [])
      .slice()
      .sort(compareSiblings)
      .filter((n) => !seen.has(n.id))
      .map((n) => {
        const next = new Set(seen);
        next.add(n.id);
        return { node: n, children: build(n.id, next) };
      });
  return build(null, new Set());
}

/** Depth-first flatten with the display depth each row should indent to. */
export function flattenHierarchyTree<N extends HierarchyNode>(
  tree: readonly HierarchyTreeNode<N>[],
): Array<{ node: N; depth: number }> {
  const out: Array<{ node: N; depth: number }> = [];
  const walk = (list: readonly HierarchyTreeNode<N>[], depth: number) => {
    for (const t of list) {
      out.push({ node: t.node, depth });
      walk(t.children, depth + 1);
    }
  };
  walk(tree, 0);
  return out;
}

/** Ancestors of a node, root first, NOT including the node itself. */
export function hierarchyAncestors<N extends HierarchyNode>(nodeId: string, nodes: readonly N[]): N[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out: N[] = [];
  const seen = new Set<string>([nodeId]);
  let cur = byId.get(nodeId)?.parentId ?? null;
  while (cur && !seen.has(cur)) {
    const n = byId.get(cur);
    if (!n) break;
    out.unshift(n);
    seen.add(cur);
    cur = n.parentId;
  }
  return out;
}

/** Ids of every descendant of a node (children, grandchildren, …). */
export function hierarchyDescendantIds(nodeId: string, nodes: readonly HierarchyNode[]): Set<string> {
  const byParent = new Map<string, string[]>();
  for (const n of nodes) {
    if (!n.parentId) continue;
    const list = byParent.get(n.parentId) ?? [];
    list.push(n.id);
    byParent.set(n.parentId, list);
  }
  const out = new Set<string>();
  const stack = [...(byParent.get(nodeId) ?? [])];
  while (stack.length) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    stack.push(...(byParent.get(id) ?? []));
  }
  return out;
}

/**
 * True when re-parenting `nodeId` under `newParentId` would create a cycle
 * (the new parent is the node itself or one of its descendants).
 */
export function wouldCreateHierarchyCycle(
  nodeId: string,
  newParentId: string | null,
  nodes: readonly HierarchyNode[],
): boolean {
  if (!newParentId) return false;
  if (newParentId === nodeId) return true;
  return hierarchyDescendantIds(nodeId, nodes).has(newParentId);
}

/** "Portfolio › Line › Product" — the node's ancestry as a breadcrumb. */
export function formatHierarchyPathLabel(
  nodeId: string,
  nodes: readonly HierarchyNode[],
  separator = ' › ',
): string {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const self = byId.get(nodeId);
  if (!self) return '';
  return [...hierarchyAncestors(nodeId, nodes), self].map((n) => n.name).join(separator);
}
